import { useState, useEffect, useRef, useCallback } from 'react';
import adminApi from '@/utils/adminApi';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageCircle, Send, User, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminChat() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const fetchConversations = useCallback(async () => {
    try {
      const response = await adminApi.get(`/chat/conversations`);
      setConversations(response.data);
    } catch (error) {
      // Non-critical - admin chat will show empty state
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to fetch conversations:', error);
      }
    }
  }, []);

  const loadMessages = useCallback(async (conversationId) => {
    try {
      const response = await adminApi.get(`/chat/messages/${conversationId}`);
      setMessages(response.data);
      
      // Mark as read
      await adminApi.put(`/chat/conversations/${conversationId}/mark-read`);
      
      // Update conversation unread count in UI
      setConversations(prev =>
        prev.map(conv =>
          conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
        )
      );
    } catch (error) {
      // Non-critical - messages will show empty state
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to load messages:', error);
      }
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    
    // Poll for new conversations every 10 seconds
    const interval = setInterval(() => {
      fetchConversations();
      if (selectedConversation) {
        loadMessages(selectedConversation.id);
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [selectedConversation, fetchConversations, loadMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const selectConversation = (conversation) => {
    setSelectedConversation(conversation);
    loadMessages(conversation.id);
  };

  const deleteConversation = async (conversationId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;

    try {
      await adminApi.delete(`/chat/conversations/${conversationId}`);
      toast.success('Conversation deleted!');
      
      // Clear selection if deleted conversation was selected
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
        setMessages([]);
      }
      
      fetchConversations();
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to delete conversation:', error);
      }
      toast.error('Failed to delete conversation');
    }
  };

  const sendReply = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    const messageData = {
      conversation_id: selectedConversation.id,
      sender: 'admin',
      sender_name: 'KittenState',
      sender_email: 'sales@kittenstate.com',
      message: newMessage.trim(),
    };

    try {
      const response = await adminApi.post(`/chat/messages`, messageData);
      setMessages([...messages, response.data]);
      setNewMessage('');
      fetchConversations(); // Refresh to update last message
    } catch {
      // Silently fail
      toast.error('Failed to send message');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendReply();
    }
  };

  const totalUnread = conversations.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);

  return (
    <div className="bg-white rounded-2xl border border-stone-100 h-[600px] flex">
      {/* Conversations List */}
      <div className="w-80 border-r border-stone-200 flex flex-col">
        <div className="p-4 border-b border-stone-200">
          <div className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#E85D75]" />
            <h3 className="text-lg font-serif font-medium text-stone-800">
              Chat Conversations
            </h3>
            {totalUnread > 0 && (
              <span className="ml-auto bg-[#E85D75] text-white text-xs px-2 py-1 rounded-full">
                {totalUnread}
              </span>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-8 text-center text-stone-500">
              <MessageCircle className="w-12 h-12 mx-auto mb-2 text-stone-300" />
              <p>No conversations yet</p>
              <p className="text-sm mt-1">Customers can chat via the website widget</p>
            </div>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.id}
                onClick={() => selectConversation(conv)}
                className={`w-full p-4 border-b border-stone-100 text-left hover:bg-stone-50 transition-colors cursor-pointer ${
                  selectedConversation?.id === conv.id ? 'bg-stone-50' : ''
                }`}
              >
                <div className="flex items-start justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-[#E85D75]/10 flex items-center justify-center flex-shrink-0">
                      <User className="w-4 h-4 text-[#E85D75]" />
                    </div>
                    <p className="font-semibold text-stone-800">{conv.customer_name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {conv.unread_count > 0 && (
                      <span className="bg-[#E85D75] text-white text-xs px-2 py-0.5 rounded-full">
                        {conv.unread_count}
                      </span>
                    )}
                    <Button
                      onClick={(e) => deleteConversation(conv.id, e)}
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 p-1 h-auto"
                      data-testid={`delete-chat-${conv.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-stone-600 truncate ml-10">{conv.last_message}</p>
                <p className="text-xs text-stone-400 mt-1 ml-10">
                  {new Date(conv.last_message_at).toLocaleString()}
                </p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className="flex-1 flex flex-col">
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-stone-200 bg-stone-50">
              <h3 className="font-semibold text-stone-800">{selectedConversation.customer_name}</h3>
              {selectedConversation.customer_email && (
                <p className="text-sm text-stone-500">{selectedConversation.customer_email}</p>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-stone-50">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] p-3 rounded-2xl ${
                      msg.sender === 'admin'
                        ? 'bg-[#E85D75] text-white rounded-br-sm'
                        : 'bg-white text-stone-700 border border-stone-200 rounded-bl-sm'
                    }`}
                  >
                    {msg.sender === 'customer' && (
                      <p className="text-xs font-semibold mb-1 text-[#E85D75]">{msg.sender_name}</p>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                    <p className="text-xs mt-1 opacity-70">
                      {new Date(msg.created_at).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Reply Input */}
            <div className="p-4 border-t border-stone-200 bg-white">
              <div className="flex gap-2">
                <Input
                  placeholder="Type your reply..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="flex-1"
                  data-testid="admin-chat-input"
                />
                <Button
                  onClick={sendReply}
                  disabled={!newMessage.trim()}
                  className="bg-[#E85D75] hover:bg-[#D64560] text-white rounded-full px-4"
                  data-testid="admin-chat-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-stone-400">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 text-stone-200" />
              <p>Select a conversation to start chatting</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
